import { FunctionComponent } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useParams, useLocation } from "wouter-preact";
import { useStore } from "@nanostores/preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $lang } from "../../store/browser_var.ts";
import { $isLogined, $isSubAccount } from "../../store/user_info.ts";
import { gotoLoginPage } from "../../router_utils.ts";
import { requestGet, requestWithParams, requestPost } from "../../utils/request.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { Section } from "../../components/ui/section.tsx";
import { Icon } from "../../components/icon/icon.tsx";
import { refreshUserInfo } from "../../store/user_info.ts";
import { invalidateCameraInfo, updateCameraInfo } from "../../store/cameras.ts";
import { usePageRefresh, useRequestScope } from "../../hooks/use_page_refresh.ts";

type SettingItemBase = {
    type: "section" | "text" | "password" | "select" | "switch" | "time";
    name?: string;
    label?: string;
    default?: string | boolean | number;
};

type SettingItemSelect = SettingItemBase & {
    type: "select";
    options: { value: string; label: string }[];
};

type SettingItem = SettingItemBase | SettingItemSelect;

type CameraSettingResponse = {
    camera_id: number;
    items: SettingItem[];
    values: Record<string, string | boolean | number>;
    status: Record<string, string>;
};

export const CameraSettingPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const lang = useStore($lang);
    const params = useParams();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const [location, _setLocation] = useLocation();

    const cameraId = Number.parseInt(params["cameraId"] ?? "-1");
    const [isBusy, setIsBusy] = useState(false);
    const [items, setItems] = useState([] as SettingItem[]);
    const [values, setValues] = useState({} as Record<string, string | boolean | number>);
    const [status, setStatus] = useState({} as Record<string, string>);
    const [dirty, setDirty] = useState(false);
    const scope = useRequestScope(`camera-setting:${cameraId}:${lang}`);

    // Use ref to break dependency cycle for toast
    const toastRef = useRef(toast);
    toastRef.current = toast;

    useEffect(() => {
        document.title = t({ zh: "相机设置", en: "Camera Settings" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? `/camera_setting/${cameraId}`);
        }
    }, [isLogined, location, cameraId]);

    const load = useMemo(() => async () => {
        if (cameraId <= 0) return;
        const request = scope.begin();
        try {
            setIsBusy(true);
            const resp = await requestGet<CameraSettingResponse>("/v2/camera_setting", { camera_id: cameraId }, request.signal);
            request.check();
            setItems(resp.items ?? []);
            setValues(resp.values ?? {});
            setStatus(resp.status ?? {});
            setDirty(false);
        } catch (e) {
            if (request.current()) toastRef.current((e as Error)?.message ?? "Request Failed", "error");
            throw e;
        } finally {
            request.finish();
            if (request.current()) setIsBusy(false);
        }
    }, [cameraId, scope]);

    const refresh = usePageRefresh({
        pageKey: `camera-setting:${cameraId}:${lang}`, enabled: isLogined,
        canAutoRefresh: !dirty && !isBusy,
        refresh: async () => {
            setIsBusy(true);
            try { await refreshUserInfo(); await load(); } finally { setIsBusy(false); }
        },
    });
    const reload = () => {
        if (!dirty || globalThis.confirm(t({ zh: "重新加载将丢弃未保存修改，继续？", en: "Discard unsaved changes and reload?" }))) {
            void refresh.refreshNow();
        }
    };

    const saveAction = useMemo(() => async () => {
        try {
            setIsBusy(true);
            await requestWithParams("PATCH", "/v2/camera_setting", {
                camera_id: cameraId,
                values: values,
            });
            setDirty(false);
            invalidateCameraInfo();
            toastRef.current(t({ zh: "保存成功", en: "Saved" }), "success");
            await Promise.all([load(), updateCameraInfo()]).catch(e =>
                toastRef.current(t({ zh: "保存成功，刷新失败：", en: "Saved; refresh failed: " }) + e.message, "warn"));
        } catch (e) {
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [cameraId, values, t, load]);

    const setValue = useMemo(() => (name: string, value: string | boolean | number) => {
        setDirty(true);
        setValues((prev) => ({ ...prev, [name]: value }));
    }, []);

    const testFtpAction = useMemo(() => async () => {
        try {
            setIsBusy(true);
            const ftpAddress = values["ftp_address"];
            const ftpUsername = values["ftp_username"];
            const ftpPassword = values["ftp_password"];
            const ftpPath = values["ftp_path"];
            
            await requestPost("/v2/camera_setting/test_ftp", {
                ftp_address: ftpAddress,
                ftp_username: ftpUsername,
                ftp_password: ftpPassword,
                ftp_path: ftpPath
            });
            toastRef.current(t({ zh: "FTP连接成功", en: "FTP Connection Successful" }), "success");
        } catch (e) {
            toastRef.current((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [values, t]);

    // Helper to group items by section
    const groupedItems = useMemo(() => {
        const groups: { label: string; items: SettingItem[] }[] = [];
        let currentGroup: { label: string; items: SettingItem[] } = { label: t({ zh: "通用设置", en: "General Settings" }), items: [] };
        
        items.forEach(item => {
            if (item.type === "section") {
                if (currentGroup.items.length > 0) {
                    groups.push(currentGroup);
                }
                currentGroup = { label: item.label ?? "", items: [] };
            } else {
                currentGroup.items.push(item);
            }
        });
        if (currentGroup.items.length > 0) {
            groups.push(currentGroup);
        }

        // Filter groups based on mode
        const mode = values["mode"];
        // Default to auto if not set yet, or check actual value
        // If mode is 'user', show User Settings, hide Day/Night
        // If mode is 'auto', hide User Settings, show Day/Night
        
        return groups.filter(g => {
            const label = g.label;
            const isUserSection = label.includes("用户设置") || label.includes("User Settings");
            const isDaySection = label.includes("日间设置") || label.includes("Day Settings");
            const isNightSection = label.includes("夜间设置") || label.includes("Night Settings");

            if (mode === "user") {
                if (isDaySection || isNightSection) return false;
            } else {
                // auto or undefined (default)
                if (isUserSection) return false;
            }
            return true;
        });
    }, [items, t, values]);

    if (!isLogined) {
        return <article aria-busy="true" class="container"></article>;
    }

    return (
        <div class="farpov_top_container">
            {refresh.error && <p role="alert">{refresh.error}</p>}
            {refresh.pendingRefresh && <p>{t({ zh: "页面已恢复，未保存修改已保留", en: "Page resumed; unsaved changes retained" })}</p>}
            <PageHeader 
                title={t({ zh: "相机设置", en: "Camera Settings" })} 
                subtitle={t({ zh: `配置相机 ID: ${cameraId}`, en: `Configure Camera ID: ${cameraId}` })}
                actions={
                    <>
                         <button class="outline secondary" disabled={isBusy || refresh.refreshing} onClick={reload}>
                            <Icon name="refresh-cw" /> {t({ zh: "刷新", en: "Refresh" })}
                        </button>
                        {!isSubAccount && (
                            <button class="primary" disabled={isBusy} onClick={saveAction}>
                                {isBusy ? <span aria-busy="true"></span> : (
                                    <>
                                        <Icon name="save" /> {t({ zh: "保存配置", en: "Save Config" })}
                                    </>
                                )}
                            </button>
                        )}
                    </>
                }
            />

            <div style={{ maxWidth: "1200px" }}>
                {/* <div class="grid" style={{ alignItems: "start", gridTemplateColumns: "2fr 1fr", gap: "2rem" }}> */}
                <div>
                    {/* Left: Settings Form */}
                    <div>
                        {groupedItems.length === 0 && items.length === 0 && !isBusy && (
                            <div class="farpov_data_grid_empty">
                                <Icon name="settings" />
                                <p>{t({ zh: "暂无配置项", en: "No settings available" })}</p>
                            </div>
                        )}

                        <form onSubmit={(e) => e.preventDefault()}>
                            {groupedItems.map((group, gIdx) => (
                                <Section key={gIdx} title={group.label}>
                                    <div class="farpov_card">
                                        {group.items.map((item, idx) => {
                                            const name = item.name ?? "";
                                            if (!name) return null;
                                            const label = item.label ?? name;
                                            const val = values[name];

                                            if (item.type === "select") {
                                                const it = item as SettingItemSelect;
                                                return (
                                                    <div class="farpov_form_group horizontal" key={idx}>
                                                        <label>{label}</label>
                                                        <select
                                                            value={typeof val === "string" ? val : String(val ?? "")}
                                                            onChange={(e) => setValue(name, (e.target as HTMLSelectElement).value)}
                                                            disabled={isBusy || isSubAccount}
                                                        >
                                                            {it.options.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                );
                                            } else if (item.type === "switch") {
                                                return (
                                                    <div class="farpov_form_group horizontal" key={idx}>
                                                        <label style={{ width: "auto", minWidth: "auto", flex: "1" }}>{label}</label>
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(val)}
                                                            onChange={(e) => setValue(name, (e.target as HTMLInputElement).checked)}
                                                            disabled={isBusy || isSubAccount}
                                                            style={{ flex: "0 0 auto", margin: 0 }}
                                                        />
                                                    </div>
                                                );
                                            } else if (item.type === "time") {
                                                return (
                                                    <div class="farpov_form_group horizontal" key={idx}>
                                                        <label>{label}</label>
                                                        <input
                                                            type="time"
                                                            step="60"
                                                            value={typeof val === "string" ? val : String(val ?? "")}
                                                            onInput={(e) => setValue(name, (e.target as HTMLInputElement).value)}
                                                            disabled={isBusy || isSubAccount}
                                                        />
                                                    </div>
                                                );
                                            } else {
                                                const inputType = item.type === "password" ? "password" : "text";
                                                return (
                                                    <div class="farpov_form_group horizontal" key={idx}>
                                                        <label>{label}</label>
                                                        <input
                                                            type={inputType}
                                                            value={typeof val === "string" ? val : String(val ?? "")}
                                                            onInput={(e) => setValue(name, (e.target as HTMLInputElement).value)}
                                                            disabled={isBusy || isSubAccount}
                                                        />
                                                    </div>
                                                );
                                            }
                                        })}
                                        {(group.label.includes("高级设置") || group.label.includes("Advanced Settings")) && (
                                            <div class="farpov_form_group" style={{ marginTop: "1rem" }}>
                                                <button type="button" class="outline" disabled={isBusy || isSubAccount} onClick={testFtpAction}>
                                                    {isBusy ? <span aria-busy="true"></span> : t({ zh: "测试 FTP 连接", en: "Test FTP Connection" })}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </Section>
                            ))}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CameraSettingPage;
